import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';

import * as Sentry from '@sentry/react';

import { useGetTimeZonesQuery } from '@api/systemValuesApiSlice';
import i18n from '@features/i18n';
import { getAvailableTimezones } from '@modules/utils/getAvailableTimezones';
import { setAppConfig } from '@store/appConfig/appConfigSlice';
import { isValidTimezone } from '@utils/isValidTimezone';
import { parseSearchParameters } from '@utils/parseUrlParameters';
import dayjs from 'dayjs';

import { useAppDispatch } from '@hooks';

const utcEquivalentTimezones = new Set([
  'Etc/UTC',
  'Etc/GMT',
  'Etc/Universal',
  'Etc/Zulu',
  'Etc/UCT',
  'Etc/Greenwich',
  'GMT',
  'Universal',
  'Zulu',
  'UCT',
  'Greenwich',
]);

const getErrorMessage = (invalidTimezone = '') =>
  invalidTimezone
    ? i18n.t('common.toast.error.tz.invalid.specified', { invalidTimezone })
    : i18n.t('common.toast.error.tz.invalid');

const getNormalizedTimezone = (timeZone: string) => {
  if (utcEquivalentTimezones.has(timeZone)) {
    return 'UTC';
  }

  const isGMT = dayjs.tz(0, timeZone).offsetName() === 'GMT';

  return isGMT ? 'UTC' : timeZone;
};

export const useInitializeTimezone = (token: string) => {
  const dispatch = useAppDispatch();
  const hasHandledQueryParameterTimezoneReference = useRef(false);

  const queryParameterTimezone = parseSearchParameters(
    window.location.search,
    'timezone'
  );
  const { data: timeZoneOptions = [], isSuccess: isTimeZonesLoaded } =
    useGetTimeZonesQuery('', {
      skip: !queryParameterTimezone || !token,
    });

  const availableTimezones = useMemo(
    () => getAvailableTimezones(timeZoneOptions),
    [timeZoneOptions]
  );

  useEffect(() => {
    const shouldSkipQueryParameterTimezoneHandling =
      !queryParameterTimezone ||
      hasHandledQueryParameterTimezoneReference.current ||
      !isTimeZonesLoaded;

    if (shouldSkipQueryParameterTimezoneHandling) {
      return;
    }

    hasHandledQueryParameterTimezoneReference.current = true;

    if (isValidTimezone(queryParameterTimezone, availableTimezones)) {
      dispatch(
        setAppConfig({
          timezone: getNormalizedTimezone(queryParameterTimezone),
        })
      );
    } else {
      const userErrorMessage = getErrorMessage();
      const sentryErrorMessage = getErrorMessage(queryParameterTimezone);

      toast.error(userErrorMessage);
      Sentry.captureException(new Error(sentryErrorMessage));
      console.error(sentryErrorMessage);
    }
  }, [availableTimezones, dispatch, isTimeZonesLoaded, queryParameterTimezone]);
};
